import Input from "@/components/Input/Input";
import { memo, useCallback } from "react";
import type { FieldRenderInputProps } from "../../types";
import type { BaseFieldProps } from "./BaseField";
import { BaseField } from "./BaseField";

interface TextFieldProps extends BaseFieldProps {
  /** 最大入力文字数 */
  maxLength?: number;

  /** 最小入力文字数 */
  minLength?: number;

  /** プレースホルダー */
  placeholder?: string;
}

export const TextField = memo(
  ({
    fieldId,
    field,
    layout,
    readOnly,
    disabled,
    className,
    maxLength,
    minLength,
    placeholder,
    "data-testid": testId,
  }: TextFieldProps) => {
    // 入力レンダリング関数
    const renderInput = useCallback(
      (props: FieldRenderInputProps) => {
        // 入力値を文字列に変換
        const stringValue =
          props.value !== undefined && props.value !== null
            ? String(props.value)
            : "";

        // 入力変更ハンドラー
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          props.onChange(e.target.value);
        };

        return (
          <Input
            id={props.id}
            name={props.name}
            type="text"
            value={stringValue}
            onChange={handleChange}
            onFocus={props.onFocus}
            onBlur={props.onBlur}
            placeholder={props.placeholder || placeholder}
            disabled={props.disabled}
            readOnly={props.readOnly}
            required={props.required}
            maxLength={maxLength}
            minLength={minLength}
            error={!!props.error}
            aria-invalid={!!props.error}
            aria-describedby={props.error ? `${props.id}-error` : undefined}
            width="100%"
            className="w-full"
            data-testid={props["data-testid"] || "input-field"}
          />
        );
      },
      [maxLength, minLength, placeholder],
    );

    return (
      <BaseField
        fieldId={fieldId}
        field={field}
        readOnly={readOnly}
        disabled={disabled}
        layout={layout}
        className={className}
        data-testid={testId}
        renderInput={renderInput}
      />
    );
  },
);

TextField.displayName = "TextField";
